import { FC, useEffect, useState } from 'react'
import cn from 'classnames'
import { useForm } from 'react-hook-form'
import { Button, Input } from 'components/ui'
import { isValidDistrictCode } from 'helpers/districts'
import { AddressService } from 'modules/address/services'
import type { AddressFormProps } from '.'
import s from './address-form.module.css'
import { useCart } from 'hooks/index'

const AddressForm: FC<AddressFormProps> = ({ className, address, recipient, onSubmit, onCancel, defaultZipCode = "" }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, touched, isSubmitting },
    getValues,
    setValue,
    setError,
  } = useForm({
    mode: 'onChange',
  })

  const { setZipCode } = useCart();

  const [zipcode, setZipcode] = useState<string>('')
  const [cityId, setCityId] = useState<number>(0)

  async function searchByZipCode() {
    const { zipCode } = getValues()
    const zip: string = zipCode.replace(/\D/g, '')

    try {
      const addressService = new AddressService()

      const { data } = await addressService.getAddressByZipcode(zip.length === 7 ? `0${zip}` : zip)
      const viacep = data?.viacep

      if (!viacep?.localidade) {
        throw new Error('CEP Inválido')
      }

      setCityId(data.city_id)
      setValue('city', viacep?.localidade)
      setValue('neighborhood', viacep?.bairro)
      setValue('district', viacep?.uf)
      setValue('street', viacep?.logradouro)
    } catch (e) {
      setError('zipCode', { message: 'CEP não encontrado' })
    }
  }

  useEffect(() => {
    if (address?.zipCode) {
      searchByZipCode()
      setZipcode(address?.zipCode.replace(/\D/g, ''))
      setZipCode(address?.zipCode.replace(/\D/g, ''))
      localStorage.setItem('selectedAddress', JSON.stringify(address));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address])

  useEffect(() => {
    if(defaultZipCode) setValue('zipCode', defaultZipCode);
  }, [defaultZipCode])

  function interceptSubmit(data: any) {
    const userName = data.recipient
    if (!userName.includes(' ')) {
      setError('recipient', {
        message: 'Informe o nome completo',
      })
      return
    }

    onSubmit({
      ...data,
      city_id: cityId,
    })
  }

  return (
    <div className={cn(s.root, className)}>
      <div className={s.title}>{address?.id ? 'Atualizar seu endereço' : 'Cadastrar novo endereço'}</div>
      <div className={s.subtitle}>
        Preencha os campos abaixo para {address?.id ? 'atualizar seu endereço' : 'cadastrar seu endereço'}
      </div>
      <form onSubmit={handleSubmit(interceptSubmit)} autoComplete="off" autoCorrect="off">
        <div className={s.zipCodeLine}>
          <Input
            type="text"
            name="recipient"
            placeholder="Quem irá receber"
            error={errors?.recipient?.message}
            touched={touched?.recipient}
            disabled={isSubmitting}
            defaultValue={recipient}
            maxLength={32}
            label="Destinatário"
            ref={register({
              required: {
                value: true,
                message: 'Informe o destinatário',
              },
              minLength: {
                value: 6,
                message: 'No mínimo 6 caracteres',
              },
            })}
          />

          <Input
            type="phone"
            name="zipCode"
            placeholder="99999-999"
            error={errors?.zipCode?.message}
            touched={touched?.zipCode}
            disabled={isSubmitting}
            defaultValue={zipcode !== '' ? zipcode : address?.zipCode}
            maxLength={8}
            onBlur={searchByZipCode}
            label="CEP"
            ref={register({
              required: {
                value: true,
                message: 'Informe o CEP',
              },
              minLength: {
                value: 7,
                message: 'CEP inválido',
              },
            })}
          />
        </div>

        <div className={s.streetline}>
          <Input
            type="text"
            name="street"
            placeholder="Rua ..."
            error={errors?.street?.message}
            touched={touched?.street}
            disabled={isSubmitting}
            defaultValue={address?.street}
            maxLength={48}
            label="Endereço"
            ref={register({
              required: {
                value: true,
                message: 'Informe seu endereço, ex.: Rua onde eu moro',
              },
              minLength: {
                value: 5,
                message: 'Ao menos 5 (cinco) caracteres',
              },
            })}
          />
          <Input
            type="text"
            name="number"
            placeholder="Número"
            error={errors?.number?.message}
            touched={touched?.number}
            disabled={isSubmitting}
            defaultValue={address?.number}
            maxLength={4}
            label="Número"
            ref={register({
              required: {
                value: true,
                message: 'Informe o número',
              },
            })}
          />
          <Input
            type="text"
            name="complement"
            placeholder="Complemento"
            error={errors?.complement?.message}
            touched={touched?.complement}
            disabled={isSubmitting}
            defaultValue={address?.complement}
            maxLength={48}
            label="Complemento"
            ref={register()}
          />
        </div>
        <div className={s.streetline}>
          <Input
            type="text"
            name="neighborhood"
            placeholder="Bairro"
            error={errors?.neighborhood?.message}
            touched={touched?.neighborhood}
            disabled={isSubmitting}
            defaultValue={address?.neighborhood}
            maxLength={48}
            label="Bairro"
            ref={register({
              required: {
                value: true,
                message: 'Informe o bairro',
              },
              minLength: {
                value: 2,
                message: 'Ao menos 2 (cinco) caracteres',
              },
            })}
          />

          <Input
            type="text"
            name="city"
            placeholder="Cidade"
            error={errors?.city?.message}
            touched={touched?.city}
            disabled={isSubmitting}
            defaultValue={address?.city?.name}
            maxLength={64}
            label="Cidade"
            ref={register({
              required: {
                value: true,
                message: 'Informe a cidade',
              },
              minLength: {
                value: 5,
                message: 'Ao menos 5 (cinco) caracteres',
              },
            })}
          />
          <Input
            type="text"
            name="district"
            placeholder="UF"
            error={errors?.district?.message}
            touched={touched?.district}
            disabled={isSubmitting}
            defaultValue={address?.city?.district}
            maxLength={2}
            label="UF"
            ref={register({
              required: {
                value: true,
                message: 'Informe o UF',
              },
              minLength: {
                value: 2,
                message: 'UF inválido',
              },
              validate: {
                validDistrict: (value) => {
                  return isValidDistrictCode(value) || 'UF não encontrado'
                },
              },
            })}
          />
        </div>
        <div className={s.footer}>
          <Button variant="ghost" onClick={onCancel} className={s.cancel}>
            Voltar
          </Button>
          <Button type="submit" disabled={isSubmitting} variant="primary" loading={isSubmitting}>
            {address?.id ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default AddressForm
